import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Search,
    Plus,
    User,
    Home,
    Building2,
    Clock,
    Grid,
    List,
    RefreshCw
} from 'lucide-react';
import { LoadingScreen } from '@/components/common/LoadingSpinner';
import { useLandlordStore, Unit } from '@/stores/useLandlordStore';
import { useToast } from '@/contexts/ToastContext';

type UnitStatus = "Available" | "Occupied" | "Maintenance" | "pending_approval";
type PaymentStatus = "Paid" | "Due" | "Overdue";
type ViewMode = 'grid' | 'list';

const AllUnits: React.FC = () => {
    const navigate = useNavigate();
    const { units, loading, fetchUnits, properties, fetchProperties, currentProperty, fetchPropertyDetails } = useLandlordStore();
    const { success, error: showError } = useToast();

    const [searchTerm, setSearchTerm] = useState('');
    const [unitFilter, setUnitFilter] = useState<UnitStatus | "All">("All");
    const [paymentFilter, setPaymentFilter] = useState<PaymentStatus | "All">("All");
    const [viewMode, setViewMode] = useState<ViewMode>('grid');
    const [isRefreshing, setIsRefreshing] = useState(false);

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            await fetchProperties();
            const allProps = properties.length > 0 ? properties : useLandlordStore.getState().properties;
            if (allProps.length > 0) {
                // Fetch units for each property
                const allUnitsPromises = allProps.map(p => fetchUnits(p.id));
                await Promise.all(allUnitsPromises);
            }
        } catch (error) {
            console.error('Failed to load units:', error);
            showError('Error', 'Failed to load units');
        }
    };

    const onRefresh = async () => {
        setIsRefreshing(true);
        try {
            await loadData();
            success('Refreshed', 'Units list has been refreshed');
        } catch (error) {
            showError('Error', 'Failed to refresh units');
        } finally {
            setIsRefreshing(false);
        }
    };

    const getStatusIndicator = (status: UnitStatus) => {
        switch (status) {
            case "Occupied":
                return { color: '#0EA5E9', icon: User, label: 'Occupied' };
            case "Available":
                return { color: '#10B981', icon: Home, label: 'Available' };
            case "Maintenance":
                return { color: '#64748B', icon: Building2, label: 'Maintenance' };
            case "pending_approval":
                return { color: '#4F46E5', icon: Clock, label: 'Pending' };
            default:
                return { color: '#64748B', icon: Home, label: status };
        }
    };

    // Get property name for a unit
    const getPropertyName = (unit: Unit) => {
        const property = properties.find(p => p.id === unit.property_id);
        return property?.name || 'Unknown Property';
    };

    const filteredUnits = useMemo(() => {
        return units.filter((unit) => {
            const matchesSearch = unit.unit_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
                (unit.description || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                getPropertyName(unit).toLowerCase().includes(searchTerm.toLowerCase());
            const matchesUnitFilter = unitFilter === "All" || unit.status === unitFilter;
            
            let matchesPaymentFilter = true;
            if (unit.status === "Occupied" && paymentFilter !== "All") {
                matchesPaymentFilter = paymentFilter === "Paid";
            }

            return matchesSearch && matchesUnitFilter && matchesPaymentFilter;
        });
    }, [units, searchTerm, unitFilter, paymentFilter, properties]);

    const handleUnitPress = (unit: Unit) => {
        navigate(`/landlord/units/detail/${unit.property_id}/${unit.id}`);
    };

    const renderFilterButton = (
        title: string,
        value: any,
        currentState: any,
        setState: any
    ) => (
        <button
            onClick={() => setState(value)}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                currentState === value
                    ? 'bg-primary text-white shadow-md'
                    : 'bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border text-light-subtle dark:text-dark-subtle hover:bg-light-bg dark:hover:bg-gray-800'
            }`}
        >
            {title}
        </button>
    );

    const renderUnitCard = (unit: Unit) => {
        const statusStyle = getStatusIndicator(unit.status);
        const StatusIcon = statusStyle.icon;
        const propertyName = getPropertyName(unit);

        return (
            <div
                key={unit.id}
                onClick={() => handleUnitPress(unit)}
                className={`bg-light-card dark:bg-dark-card rounded-xl shadow-sm border border-light-border dark:border-dark-border cursor-pointer hover:shadow-lg transition-all duration-200 group relative ${
                    viewMode === 'grid' ? 'p-5' : 'p-4 flex items-center gap-4'
                }`}
            >
                {/* Status Color Bar */}
                <div
                    className="absolute left-0 top-0 bottom-0 w-1 rounded-l-xl"
                    style={{ backgroundColor: statusStyle.color }}
                />

                <div className="flex-1">
                    <div className={viewMode === 'list' ? 'flex items-center gap-4' : ''}>
                        <div className={viewMode === 'list' ? 'flex-1' : ''}>
                            <div className={`mb-3 ${viewMode === 'list' ? 'flex items-center gap-4 mb-0' : ''}`}>
                                <div>
                                    <h3 className="text-lg font-bold text-light-text dark:text-dark-text">
                                        Unit {unit.unit_number}
                                    </h3>
                                    <p className="text-sm text-light-subtle dark:text-dark-subtle">
                                        {unit.unit_type} • {unit.bedrooms || 0} Bed, {unit.bathrooms || 0} Bath
                                    </p>
                                    <p className="text-xs text-primary mt-1 flex items-center gap-1">
                                        <Building2 size={12} />
                                        {propertyName}
                                    </p>
                                </div>
                                <div className={viewMode === 'list' ? 'text-right' : 'text-right'}>
                                    <p className="text-lg font-bold text-primary">
                                        KES {unit.rent_amount.toLocaleString()}
                                    </p>
                                    <p className="text-xs text-light-subtle dark:text-dark-subtle">per month</p>
                                </div>
                            </div>

                            {/* Status Chips */}
                            <div className={`flex flex-wrap items-center gap-2 ${viewMode === 'list' ? 'mb-0' : 'mb-3'}`}>
                                <div
                                    className="flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium"
                                    style={{
                                        backgroundColor: `${statusStyle.color}20`,
                                        color: statusStyle.color
                                    }}
                                >
                                    <StatusIcon size={12} />
                                    <span>{statusStyle.label}</span>
                                </div>

                                {unit.has_pending_invitation && (
                                    <div className="flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-warning/20 text-warning">
                                        <Clock size={12} />
                                        <span>Lease Pending</span>
                                    </div>
                                )}
                            </div>

                            {/* Current Tenant */}
                            {unit.current_tenant && viewMode === 'grid' && (
                                <div className="flex items-center gap-3 p-3 bg-light-bg dark:bg-dark-bg rounded-lg mt-3">
                                    <img
                                        src={unit.current_tenant.avatar_url || "https://i.pravatar.cc/150?u=default"}
                                        alt={unit.current_tenant.full_name}
                                        className="w-10 h-10 rounded-full"
                                    />
                                    <div className="flex-1">
                                        <p className="font-medium text-light-text dark:text-dark-text text-sm">
                                            {unit.current_tenant.full_name}
                                        </p>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    // Stats
    const stats = useMemo(() => ({
        total: units.length,
        available: units.filter(u => u.status === 'Available').length,
        occupied: units.filter(u => u.status === 'Occupied').length,
        maintenance: units.filter(u => u.status === 'Maintenance').length,
        pending: units.filter(u => u.status === 'pending_approval').length
    }), [units]);

    if (loading && units.length === 0) {
        return <LoadingScreen />;
    }

    return (
        <div className="min-h-screen bg-light-bg dark:bg-dark-bg pb-20">
            {/* Header */}
            <div className="bg-light-card dark:bg-dark-card border-b border-light-border dark:border-dark-border px-4 md:px-6 py-4 sticky top-0 z-10">
                <div className="flex items-center justify-between mb-4">
                    <div>
                        <h1 className="text-2xl font-bold text-light-text dark:text-dark-text">
                            All Units
                        </h1>
                        <p className="text-sm text-light-subtle dark:text-dark-subtle">
                            {filteredUnits.length} unit{filteredUnits.length !== 1 ? 's' : ''} across {properties.length} propert{properties.length !== 1 ? 'ies' : 'y'}
                        </p>
                    </div>
                    <button
                        onClick={onRefresh}
                        disabled={isRefreshing}
                        className="p-2 hover:bg-light-bg dark:hover:bg-gray-800 rounded-full transition-colors"
                        title="Refresh"
                    >
                        <RefreshCw size={20} className={`text-light-subtle dark:text-dark-subtle ${isRefreshing ? 'animate-spin' : ''}`} />
                    </button>
                </div>

                {/* Stats Cards */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
                    <div className="bg-light-bg dark:bg-dark-bg p-3 rounded-xl">
                        <p className="text-xs text-light-subtle dark:text-dark-subtle">Total</p>
                        <p className="text-xl font-bold text-light-text dark:text-dark-text">{stats.total}</p>
                    </div>
                    <div className="bg-green-50 dark:bg-green-900/20 p-3 rounded-xl">
                        <p className="text-xs text-green-600 dark:text-green-400">Available</p>
                        <p className="text-xl font-bold text-green-700 dark:text-green-400">{stats.available}</p>
                    </div>
                    <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-xl">
                        <p className="text-xs text-blue-600 dark:text-blue-400">Occupied</p>
                        <p className="text-xl font-bold text-blue-700 dark:text-blue-400">{stats.occupied}</p>
                    </div>
                    <div className="bg-gray-50 dark:bg-gray-800 p-3 rounded-xl">
                        <p className="text-xs text-gray-600 dark:text-gray-400">Maintenance</p>
                        <p className="text-xl font-bold text-gray-700 dark:text-gray-400">{stats.maintenance}</p>
                    </div>
                    <div className="bg-indigo-50 dark:bg-indigo-900/20 p-3 rounded-xl">
                        <p className="text-xs text-indigo-600 dark:text-indigo-400">Pending</p>
                        <p className="text-xl font-bold text-indigo-700 dark:text-indigo-400">{stats.pending}</p>
                    </div>
                </div>

                {/* Search Bar */}
                <div className="relative mb-4">
                    <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-light-subtle dark:text-dark-subtle" />
                    <input
                        type="text"
                        placeholder="Search units by number, description, or property..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-10 pr-4 py-3 bg-light-input dark:bg-dark-input border border-light-border dark:border-dark-border rounded-xl text-light-text dark:text-dark-text placeholder-light-placeholder dark:placeholder-dark-placeholder focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all"
                    />
                </div>

                {/* Filters Row */}
                <div className="flex flex-wrap items-center gap-3">
                    {/* Unit Status Filters */}
                    <div className="flex flex-wrap gap-2">
                        {renderFilterButton("All", "All", unitFilter, setUnitFilter)}
                        {renderFilterButton("Occupied", "Occupied", unitFilter, setUnitFilter)}
                        {renderFilterButton("Available", "Available", unitFilter, setUnitFilter)}
                        {renderFilterButton("Maintenance", "Maintenance", unitFilter, setUnitFilter)}
                    </div>

                    {/* View Mode Toggle */}
                    <div className="ml-auto flex items-center gap-2 bg-light-input dark:bg-dark-input rounded-lg p-1">
                        <button
                            onClick={() => setViewMode('grid')}
                            className={`p-2 rounded-lg transition-colors ${
                                viewMode === 'grid'
                                    ? 'bg-primary text-white'
                                    : 'text-light-subtle dark:text-dark-subtle hover:bg-light-bg dark:hover:bg-gray-800'
                            }`}
                            title="Grid View"
                        >
                            <Grid size={18} />
                        </button>
                        <button
                            onClick={() => setViewMode('list')}
                            className={`p-2 rounded-lg transition-colors ${
                                viewMode === 'list'
                                    ? 'bg-primary text-white'
                                    : 'text-light-subtle dark:text-dark-subtle hover:bg-light-bg dark:hover:bg-gray-800'
                            }`}
                            title="List View"
                        >
                            <List size={18} />
                        </button>
                    </div>
                </div>
            </div>

            {/* Units Grid/List */}
            <div className="p-4 md:p-6">
                {filteredUnits.length === 0 ? (
                    <div className="text-center py-16">
                        <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-light-card dark:bg-dark-card mb-4">
                            <Home size={40} className="text-light-subtle dark:text-dark-subtle" />
                        </div>
                        <h3 className="text-xl font-bold text-light-text dark:text-dark-text mb-2">
                            No units found
                        </h3>
                        <p className="text-light-subtle dark:text-dark-subtle mb-6 max-w-md mx-auto">
                            {searchTerm || unitFilter !== "All" 
                                ? "No units match your current filters. Try adjusting your search or filters."
                                : "You haven't added any units yet. Get started by adding your first unit."
                            }
                        </p>
                        {!searchTerm && unitFilter === "All" && (
                            <button
                                onClick={() => navigate('/landlord/units/register')}
                                className="inline-flex items-center gap-2 bg-primary text-white px-6 py-3 rounded-xl font-medium hover:bg-primary/90 transition-colors"
                            >
                                <Plus size={20} />
                                Add Your First Unit
                            </button>
                        )}
                    </div>
                ) : (
                    <div className={viewMode === 'grid' 
                        ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4'
                        : 'flex flex-col gap-3'
                    }>
                        {filteredUnits.map((unit) => renderUnitCard(unit))}
                    </div>
                )}
            </div>

            {/* Floating Add Button */}
            <button
                onClick={() => navigate('/landlord/units/register')}
                className="fixed bottom-6 right-6 bg-primary hover:bg-primary/90 text-white p-4 rounded-full shadow-2xl transition-all hover:scale-105 active:scale-95 z-20"
                title="Add New Unit"
            >
                <Plus size={24} />
            </button>
        </div>
    );
};

export default AllUnits;